import apiService from './api';
import type { ApiResponse } from '../types';

export interface DaySchedule {
  day_of_week: number; // 1=Mon … 7=Sun
  start_time: string | null;
  end_time: string | null;
  lunch_duration_minutes: number | null;
  // When lunch starts. With it, clock-out deducts only the part of the break a
  // session covers; without it, the whole break off any session over the
  // threshold. On a user's day, null inherits the organisation's.
  lunch_start_time?: string | null;
  is_working_day: boolean | null;
  source?: 'user' | 'org' | 'none';
}

export interface OrgDaySchedule {
  day_of_week: number;
  start_time: string;
  end_time: string;
  lunch_duration_minutes: number;
  lunch_start_time?: string | null;
  is_working_day: boolean;
}

export interface LeaveHoursPreview {
  start_date: string;
  end_date: string;
  working_days: number;
  scheduled_hours: number;
}

export const DAY_NAMES = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

class WorkScheduleService {
  // Get the org-level default schedule (7 day entries)
  async getOrgSchedule(orgId: number): Promise<ApiResponse<OrgDaySchedule[]>> {
    return apiService.get(`/organisations/${orgId}/work-schedule`);
  }

  // Update the org-level default schedule
  async updateOrgSchedule(orgId: number, schedule: OrgDaySchedule[]): Promise<ApiResponse<OrgDaySchedule[]>> {
    return apiService.put(`/organisations/${orgId}/work-schedule`, { schedule });
  }

  // Get the effective work schedule for a user (merged org + user overrides).
  // Response body also carries `clock_grace_minutes` (per-user override or null)
  // and `effective_clock_grace_minutes` (user → org default) alongside `data`.
  async getUserSchedule(userId: number): Promise<ApiResponse<DaySchedule[]> & {
    clock_grace_minutes?: number | null;
    effective_clock_grace_minutes?: number;
  }> {
    return apiService.get(`/users/${userId}/work-schedule`);
  }

  // Update per-user work schedule overrides (admin only).
  // Pass `clockGraceMinutes = null` to clear the user override and inherit the org default.
  async updateUserSchedule(
    userId: number,
    schedule: DaySchedule[],
    clockGraceMinutes?: number | null
  ): Promise<ApiResponse<DaySchedule[]> & {
    clock_grace_minutes?: number | null;
    effective_clock_grace_minutes?: number;
  }> {
    const payload = schedule.map(({ source: _source, ...rest }) => rest);
    const body: Record<string, unknown> = { schedule: payload };
    if (clockGraceMinutes !== undefined) {
      body.clock_grace_minutes = clockGraceMinutes;
    }
    return apiService.put(`/users/${userId}/work-schedule`, body);
  }

  // Preview how many scheduled hours a leave period will consume
  async previewLeaveHours(startDate: string, endDate: string): Promise<ApiResponse<LeaveHoursPreview>> {
    return apiService.get(`/leave/preview-hours?start_date=${startDate}&end_date=${endDate}`);
  }

  // Format a time string 'HH:MM' to human-readable '7:00 AM'
  formatTime(time: string | null): string {
    if (!time) return '—';
    const [hourStr, minStr] = time.split(':');
    const hour = parseInt(hourStr);
    const min = minStr || '00';
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${min} ${ampm}`;
  }
}

export const workScheduleService = new WorkScheduleService();
export default workScheduleService;
